import { objectKeysToCamelCase, objectKeysToSnakeCase } from 'utils/common';
import axiosWithToken from './axios-with-token';
import { Region } from './regions';

export interface Portal {
  name: string;
  displayName: string;
  logo: string;
  link: string;
  portalType: string;
  terminals: { key: string; name: string }[];
}

export interface TerminalCredential {
  id: string;
  login: string;
  status:
    | 'valid'
    | 'failed'
    | 'invalid'
    | 'expired'
    | 'not_verified'
    | 'frozen'
    | 'restricted'
    | 'missing';
  portal: Portal;
}

export interface TerminalCredentialResponse {
  count: number;
  next: string;
  previous: string | null;
  results: TerminalCredential[];
}

interface CredentialFilters {
  page: number;
  pageSize: number;
  sortBy: string;
  sortOrder: string;
  portCollectionCodes: string;
}

export async function getTerminalCredentials(
  filters: Partial<CredentialFilters>,
  signal?: AbortSignal
): Promise<TerminalCredentialResponse> {
  const response = await axiosWithToken.get<TerminalCredentialResponse>(
    '/core/api/v1/appointments/terminal-credentials',
    {
      signal,
      params: objectKeysToSnakeCase(filters),
    }
  );
  return objectKeysToCamelCase(response.data);
}

interface MissingCredentialsResponse {
  missing: number;
  total: number;
}

export async function getMissingCredentials(
  portCollectionCode: string,
  signal?: AbortSignal
): Promise<MissingCredentialsResponse> {
  const response = await axiosWithToken.get<MissingCredentialsResponse>(
    '/core/api/v1/appointments/missing-credentials',
    { signal, params: objectKeysToSnakeCase({ portCollectionCode }) }
  );
  return objectKeysToCamelCase(response.data);
}

export async function checkCredentialsForRegion(region: Region): Promise<boolean> {
  try {
    const resp = await getMissingCredentials(region.code);
    return resp.missing === 0;
  } catch {
    // this may fail with a 403, in which cse do nothing
  }
  return true;
}

export type CredentialUpdateCode =
  | 'authentication_failed'
  | 'invalid_credential'
  | 'credential_already_exists'
  | 'unknown_error'
  | 'success';

interface CredentialResponse {
  code: CredentialUpdateCode;
}

export async function updateCredential(variables: {
  credentialId: string;
  login: string;
  password: string;
}): Promise<void> {
  const { credentialId, login, password } = variables;
  await axiosWithToken.put<CredentialResponse>(
    `/core/api/v1/appointments/terminal-credential/${credentialId}`,
    { login, password }
  );
}

export async function createCredential(variables: {
  portalName: string;
  login: string;
  password: string;
}): Promise<void> {
  await axiosWithToken.post(
    '/core/api/v1/appointments/terminal-credentials',
    objectKeysToSnakeCase(variables)
  );
}

export async function actOnCredentials(variables: {
  credentials: string[];
  action: 'delete';
}): Promise<void> {
  await axiosWithToken.post('/core/api/v1/appointments/terminal-credentials/action', variables);
}

interface GetPortalsResponse {
  results: Portal[];
}

export async function getPortals(variables: { portCollectionCode: string }, signal?: AbortSignal) {
  const { data } = await axiosWithToken.get<GetPortalsResponse>('/core/api/v1/erl/portals', {
    params: { page_size: 50, port_collection_codes: variables.portCollectionCode },
    signal,
  });
  const portals: Portal[] = objectKeysToCamelCase(data.results || []);
  return portals;
}
